"use client"

import { Box, Link } from "@chakra-ui/react"
import Image from "next/image"
import {
  type CSSProperties,
  type Key,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface LogoNodeItem {
  node: ReactNode
  title?: string
  ariaLabel?: string
  href?: string
}

export interface LogoImageItem {
  src: string
  srcSet?: string
  sizes?: string
  width?: number
  height?: number
  alt?: string
  title?: string
  href?: string
}

export type LogoItem = LogoNodeItem | LogoImageItem

export interface LogoLoopProps {
  logos: LogoItem[]
  speed?: number
  direction?: "left" | "right" | "up" | "down"
  width?: number | string
  logoHeight?: number
  gap?: number
  /** @deprecated Use hoverSpeed instead */
  pauseOnHover?: boolean
  hoverSpeed?: number
  fadeOut?: boolean
  fadeOutColor?: string
  scaleOnHover?: boolean
  renderItem?: (item: LogoItem, key: Key) => ReactNode
  ariaLabel?: string
  className?: string
  style?: CSSProperties
}

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const ANIMATION_CONFIG = {
  COPY_HEADROOM: 2,
  MIN_COPIES: 2,
  SMOOTH_TAU: 0.25,
}

const toCssLength = (value: number | string | undefined) =>
  typeof value === "number" ? `${value}px` : (value ?? undefined)

const isRawImageSource = (src: string) =>
  src.startsWith("/") || /^(?:https?:)?\/\//.test(src)

const shouldUseUnoptimizedImage = (item: LogoImageItem) =>
  Boolean(item.srcSet) ||
  isRawImageSource(item.src) ||
  item.src.toLowerCase().split("?")[0]?.endsWith(".svg")

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

function useResizeObserver(
  callback: () => void,
  elements: React.RefObject<HTMLElement | null>[],
  dependencies: unknown[]
) {
  useEffect(() => {
    if (!window.ResizeObserver) {
      const handleResize = () => callback()
      window.addEventListener("resize", handleResize)
      callback()
      return () => window.removeEventListener("resize", handleResize)
    }
    const observers = elements.map((ref) => {
      if (!ref.current) return null
      const observer = new ResizeObserver(callback)
      observer.observe(ref.current)
      return observer
    })
    callback()
    return () => {
      observers.forEach((observer) => observer?.disconnect())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, ...dependencies])
}

function useImageLoader(
  seqRef: React.RefObject<HTMLElement | null>,
  onLoad: () => void,
  dependencies: unknown[]
) {
  useEffect(() => {
    const images =
      seqRef.current?.querySelectorAll<HTMLImageElement>("img") ?? []
    if (images.length === 0) {
      onLoad()
      return
    }
    let remainingImages = images.length
    const handleImageLoad = () => {
      remainingImages -= 1
      if (remainingImages === 0) onLoad()
    }
    images.forEach((img) => {
      if (img.complete) {
        handleImageLoad()
      } else {
        img.addEventListener("load", handleImageLoad, { once: true })
        img.addEventListener("error", handleImageLoad, { once: true })
      }
    })
    return () => {
      images.forEach((img) => {
        img.removeEventListener("load", handleImageLoad)
        img.removeEventListener("error", handleImageLoad)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoad, seqRef, ...dependencies])
}

function useAnimationLoop(
  trackRef: React.RefObject<HTMLElement | null>,
  targetVelocity: number,
  seqWidth: number,
  seqHeight: number,
  isHovered: boolean,
  hoverSpeed: number | undefined,
  isVertical: boolean
) {
  const rafRef = useRef<number | null>(null)
  const lastTimestampRef = useRef<number | null>(null)
  const offsetRef = useRef(0)
  const velocityRef = useRef(0)

  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    const seqSize = isVertical ? seqHeight : seqWidth

    if (seqSize > 0) {
      offsetRef.current = ((offsetRef.current % seqSize) + seqSize) % seqSize
      const transformValue = isVertical
        ? `translate3d(0, ${-offsetRef.current}px, 0)`
        : `translate3d(${-offsetRef.current}px, 0, 0)`
      track.style.transform = transformValue
    }

    const animate = (timestamp: number) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp
      }

      const deltaTime = Math.max(0, timestamp - lastTimestampRef.current) / 1000
      lastTimestampRef.current = timestamp

      const target =
        isHovered && hoverSpeed !== undefined ? hoverSpeed : targetVelocity

      const easingFactor =
        1 - Math.exp(-deltaTime / ANIMATION_CONFIG.SMOOTH_TAU)
      velocityRef.current += (target - velocityRef.current) * easingFactor

      if (seqSize > 0) {
        let nextOffset = offsetRef.current + velocityRef.current * deltaTime
        nextOffset = ((nextOffset % seqSize) + seqSize) % seqSize
        offsetRef.current = nextOffset

        const transformValue = isVertical
          ? `translate3d(0, ${-offsetRef.current}px, 0)`
          : `translate3d(${-offsetRef.current}px, 0, 0)`
        track.style.transform = transformValue
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      lastTimestampRef.current = null
    }
  }, [
    targetVelocity,
    seqWidth,
    seqHeight,
    isHovered,
    hoverSpeed,
    isVertical,
    trackRef,
  ])
}

/* ------------------------------------------------------------------ */
/* Styles (converted from CSS to Chakra-compatible objects)             */
/* ------------------------------------------------------------------ */

const itemBaseCss = {
  "& img": {
    display: "block",
    height: "var(--logoloop-logoHeight)",
    imageRendering:
      "-webkit-optimize-contrast" as CSSProperties["imageRendering"],
    objectFit: "contain" as const,
    pointerEvents: "none" as const,
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    WebkitUserDrag: "none",
    width: "auto",
  },
  flex: "0 0 auto",

  lineHeight: 1,
}

const scaleHoverItemCss = {
  "&:hover img, &:hover .logoloop__node": {
    transform: "scale(1.2)",
    transformOrigin: "center center",
  },
  overflow: "visible",
}

const fadeBeforeAfterBase: CSSProperties = {
  bottom: 0,
  content: '""',
  pointerEvents: "none",
  position: "absolute",
  top: 0,
  width: "clamp(24px, 8%, 120px)",
  zIndex: 10,
}

interface LogoLoopItemProps {
  gap: number
  isVertical: boolean
  item: LogoItem
  itemKey: Key
  logoHeight: number
  renderItem?: (item: LogoItem, key: Key) => ReactNode
  scaleOnHover: boolean
}

const LogoLoopItem = ({
  gap,
  isVertical,
  item,
  itemKey,
  logoHeight,
  renderItem: customRenderer,
  scaleOnHover,
}: LogoLoopItemProps) => {
  if (customRenderer) {
    return (
      <Box
        as="li"
        css={itemBaseCss}
        fontSize={`${logoHeight}px`}
        mb={isVertical ? `${gap}px` : 0}
        mr={isVertical ? 0 : `${gap}px`}
        role="listitem"
      >
        {customRenderer(item, itemKey)}
      </Box>
    )
  }

  const isNodeItem = "node" in item
  const imageSize = Math.max(1, Math.round(logoHeight))
  const content = isNodeItem ? (
    <Box
      alignItems="center"
      aria-hidden={!!item.href && !item.ariaLabel}
      as="span"
      className="logoloop__node"
      display="inline-flex"
      transition="transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
    >
      {item.node}
    </Box>
  ) : (
    <Image
      alt={item.alt ?? ""}
      decoding="async"
      draggable={false}
      height={item.height ?? imageSize}
      loading="lazy"
      sizes={item.sizes}
      src={item.src}
      title={item.title}
      unoptimized={shouldUseUnoptimizedImage(item)}
      width={item.width ?? imageSize}
    />
  )

  const itemAriaLabel = isNodeItem
    ? (item.ariaLabel ?? item.title)
    : (item.alt ?? item.title)

  const itemContent = item.href ? (
    <Link
      _focusVisible={{
        outline: "2px solid currentColor",
        outlineOffset: "2px",
      }}
      _hover={{ opacity: 0.8 }}
      alignItems="center"
      aria-label={itemAriaLabel || "logo link"}
      borderRadius="4px"
      display="inline-flex"
      href={item.href}
      rel="noreferrer noopener"
      target="_blank"
      textDecoration="none"
      transition="opacity 0.2s ease"
    >
      {content}
    </Link>
  ) : (
    content
  )

  return (
    <Box
      as="li"
      css={{
        ...itemBaseCss,
        ...(scaleOnHover ? scaleHoverItemCss : {}),
      }}
      fontSize={`${logoHeight}px`}
      mb={isVertical ? `${gap}px` : 0}
      mr={isVertical ? 0 : `${gap}px`}
      role="listitem"
    >
      {itemContent}
    </Box>
  )
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const LogoLoop = memo(function LogoLoop({
  ariaLabel = "Partner logos",
  className,
  direction = "left",
  fadeOut = false,
  fadeOutColor,
  gap = 32,
  hoverSpeed,
  logoHeight = 28,
  logos,
  pauseOnHover,
  renderItem,
  scaleOnHover = false,
  speed = 120,
  style,
  width = "100%",
}: LogoLoopProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const seqRef = useRef<HTMLUListElement>(null)

  const [seqWidth, setSeqWidth] = useState(0)
  const [seqHeight, setSeqHeight] = useState(0)
  const [copyCount, setCopyCount] = useState(ANIMATION_CONFIG.MIN_COPIES)
  const [isHovered, setIsHovered] = useState(false)

  const effectiveHoverSpeed = useMemo(() => {
    if (hoverSpeed !== undefined) return hoverSpeed
    if (pauseOnHover === true) return 0
    if (pauseOnHover === false) return undefined
    return 0
  }, [hoverSpeed, pauseOnHover])

  const isVertical = direction === "up" || direction === "down"

  const targetVelocity = useMemo(() => {
    const magnitude = Math.abs(speed)
    let directionMultiplier: number
    if (isVertical) {
      directionMultiplier = direction === "up" ? 1 : -1
    } else {
      directionMultiplier = direction === "left" ? 1 : -1
    }
    const speedMultiplier = speed < 0 ? -1 : 1
    return magnitude * directionMultiplier * speedMultiplier
  }, [speed, direction, isVertical])

  const updateDimensions = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth ?? 0
    const sequenceRect = seqRef.current?.getBoundingClientRect?.()
    const sequenceWidth = sequenceRect?.width ?? 0
    const sequenceHeight = sequenceRect?.height ?? 0
    if (isVertical) {
      const parentHeight =
        containerRef.current?.parentElement?.clientHeight ?? 0
      if (containerRef.current && parentHeight > 0) {
        const targetHeight = Math.ceil(parentHeight)
        if (containerRef.current.style.height !== `${targetHeight}px`)
          containerRef.current.style.height = `${targetHeight}px`
      }
      if (sequenceHeight > 0) {
        setSeqHeight(Math.ceil(sequenceHeight))
        const viewport =
          containerRef.current?.clientHeight ?? parentHeight ?? sequenceHeight
        const copiesNeeded =
          Math.ceil(viewport / sequenceHeight) + ANIMATION_CONFIG.COPY_HEADROOM
        setCopyCount(Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded))
      }
    } else if (sequenceWidth > 0) {
      setSeqWidth(Math.ceil(sequenceWidth))
      const copiesNeeded =
        Math.ceil(containerWidth / sequenceWidth) +
        ANIMATION_CONFIG.COPY_HEADROOM
      setCopyCount(Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded))
    }
  }, [isVertical])

  useResizeObserver(
    updateDimensions,
    [containerRef, seqRef],
    [logos, gap, logoHeight, isVertical]
  )

  useImageLoader(seqRef, updateDimensions, [logos, gap, logoHeight, isVertical])

  useAnimationLoop(
    trackRef,
    targetVelocity,
    seqWidth,
    seqHeight,
    isHovered,
    effectiveHoverSpeed,
    isVertical
  )

  const handleMouseEnter = useCallback(() => {
    if (effectiveHoverSpeed !== undefined) setIsHovered(true)
  }, [effectiveHoverSpeed])

  const handleMouseLeave = useCallback(() => {
    if (effectiveHoverSpeed !== undefined) setIsHovered(false)
  }, [effectiveHoverSpeed])

  const logoLists = useMemo(
    () =>
      Array.from({ length: copyCount }, (_, copyIndex) => (
        <Box
          alignItems="center"
          as="ul"
          display="flex"
          flexDirection={isVertical ? "column" : "row"}
          inert={copyIndex > 0 ? true : undefined}
          key={`copy-${copyIndex}`}
          listStyleType="none"
          m={0}
          p={0}
          pointerEvents={copyIndex > 0 ? "none" : undefined}
          ref={copyIndex === 0 ? seqRef : undefined}
          role="list"
        >
          {logos.map((item, itemIndex) => {
            const itemKey = `${copyIndex}-${itemIndex}`

            return (
              <LogoLoopItem
                gap={gap}
                isVertical={isVertical}
                item={item}
                itemKey={itemKey}
                key={itemKey}
                logoHeight={logoHeight}
                renderItem={renderItem}
                scaleOnHover={scaleOnHover}
              />
            )
          })}
        </Box>
      )),
    [copyCount, gap, isVertical, logoHeight, logos, renderItem, scaleOnHover]
  )

  /* ---- Fade pseudo-element styles ---- */

  const fadeCss = useMemo(() => {
    if (!fadeOut) return {}

    const fadeColor =
      fadeOutColor ?? "var(--logoloop-fadeColorAuto, transparent)"

    if (isVertical) {
      return {
        _after: {
          ...fadeBeforeAfterBase,
          background: `linear-gradient(to top, ${fadeColor} 0%, rgba(0,0,0,0) 100%)`,
          height: "clamp(24px, 8%, 120px)",
          left: 0,
          right: 0,
          top: "auto",
          width: "100%",
        },
        _before: {
          ...fadeBeforeAfterBase,
          background: `linear-gradient(to bottom, ${fadeColor} 0%, rgba(0,0,0,0) 100%)`,
          bottom: "auto",
          height: "clamp(24px, 8%, 120px)",
          left: 0,
          right: 0,
          width: "100%",
        },
      }
    }

    return {
      _after: {
        ...fadeBeforeAfterBase,
        background: `linear-gradient(to left, ${fadeColor} 0%, rgba(0,0,0,0) 100%)`,
        left: "auto",
        right: 0,
      },
      _before: {
        ...fadeBeforeAfterBase,
        background: `linear-gradient(to right, ${fadeColor} 0%, rgba(0,0,0,0) 100%)`,
        left: 0,
      },
    }
  }, [fadeOut, fadeOutColor, isVertical])

  const containerStyle: CSSProperties = useMemo(
    () =>
      ({
        "--logoloop-gap": `${gap}px`,
        "--logoloop-logoHeight": `${logoHeight}px`,
        ...(fadeOutColor && { "--logoloop-fadeColor": fadeOutColor }),

        width: isVertical
          ? toCssLength(width) === "100%"
            ? undefined
            : toCssLength(width)
          : (toCssLength(width) ?? "100%"),

        ...style,
      }) as CSSProperties,
    [width, gap, logoHeight, fadeOutColor, style, isVertical]
  )

  return (
    <Box
      aria-label={ariaLabel}
      className={className}
      css={fadeCss}
      display={isVertical ? "inline-block" : undefined}
      height={isVertical ? "100%" : undefined}
      overflow="hidden"
      pb={scaleOnHover ? `calc(${logoHeight}px * 0.1)` : undefined}
      position="relative"
      pt={scaleOnHover ? `calc(${logoHeight}px * 0.1)` : undefined}
      ref={containerRef}
      role="region"
      style={containerStyle}
    >
      <Box
        display="flex"
        flexDirection={isVertical ? "column" : "row"}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        position="relative"
        ref={trackRef}
        userSelect="none"
        width="max-content"
        willChange="transform"
        zIndex={0}
      >
        {logoLists}
      </Box>
    </Box>
  )
})

LogoLoop.displayName = "LogoLoop"

export default LogoLoop
